export enum RightBundle {
  FREE = 'FREE',
  LITE = 'LITE',
  STARTER = 'STARTER',
  BASIC = 'BASIC',
  PRO = 'PRO',
  PREMIUM = 'PREMIUM',
}

export type VersionType = 'v1' | 'v2';

/**
 * 老用户截止时间（UTC+8 时区，即北京时间）
 * 创建时间早于此时间点的用户被认为是老用户，会显示 ChangelogModal
 * 格式：与后端返回的 createDate 格式保持一致（无时区标识，实际为 UTC+8）
 */
export const LEGACY_USER_CUTOFF_DATE = '2025-11-24 00:00:00';

export interface ChangelogModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (version: VersionType) => void;
  // 用户权益类型,用于显示不同的文案
  userProductKey?: RightBundle;
  // 是否显示 Debug 面板
  showDebugPanel?: boolean;
}

export interface BenefitItem {
  titleKey: string;
  detailKey?: string;
}

export interface UserTypeConfig {
  // 描述文案的 i18n key
  descriptionKey: string;
  // Benefits 配置 - 每个用户类型的 benefits 列表
  benefits: BenefitItem[];
}

// 降级映射策略: LITE/BASIC -> STARTER, PRO -> PREMIUM
export const USER_TYPE_FALLBACK_MAP: Partial<Record<RightBundle, RightBundle>> = {
  [RightBundle.LITE]: RightBundle.STARTER,
  [RightBundle.BASIC]: RightBundle.STARTER,
  [RightBundle.PRO]: RightBundle.PREMIUM,
};

// 默认配置 (用于未知用户类型)
export const DEFAULT_USER_CONFIG: UserTypeConfig = {
  descriptionKey: 'changelog.description',
  benefits: [
    { titleKey: 'changelog.benefitsSynced', detailKey: 'changelog.benefitsSyncedDetail' },
    { titleKey: 'changelog.benefitsSwitch', detailKey: 'changelog.benefitsSwitchDetail' },
    { titleKey: 'changelog.benefitsIndependent', detailKey: 'changelog.benefitsIndependentDetail' },
  ],
};

// 用户类型配置映射
export const USER_TYPE_CONFIG: Partial<Record<RightBundle, UserTypeConfig>> = {
  [RightBundle.FREE]: {
    descriptionKey: 'changelog.descriptionFree',
    benefits: [
      { titleKey: 'changelog.benefitsSwitchFree', detailKey: 'changelog.benefitsSwitchDetailFree' },
      { titleKey: 'changelog.benefitsIndependentFree', detailKey: 'changelog.benefitsIndependentDetailFree' },
    ],
  },
  [RightBundle.STARTER]: {
    descriptionKey: 'changelog.description',
    benefits: [
      { titleKey: 'changelog.benefitsSynced', detailKey: 'changelog.benefitsSyncedDetail' },
      { titleKey: 'changelog.benefitsSwitch', detailKey: 'changelog.benefitsSwitchDetail' },
      { titleKey: 'changelog.benefitsIndependent', detailKey: 'changelog.benefitsIndependentDetail' },
    ],
  },
  [RightBundle.PREMIUM]: {
    descriptionKey: 'changelog.descriptionPremium',
    benefits: [
      { titleKey: 'changelog.benefitsSynced', detailKey: 'changelog.benefitsSyncedDetail' },
      { titleKey: 'changelog.benefitsSwitch', detailKey: 'changelog.benefitsSwitchDetail' },
      { titleKey: 'changelog.benefitsIndependent', detailKey: 'changelog.benefitsIndependentDetail' },
    ],
  },
};

// 获取用户配置的辅助函数
export function getUserConfig(productKey?: RightBundle): UserTypeConfig {
  if (!productKey) {
    return DEFAULT_USER_CONFIG;
  }

  // 先尝试直接获取配置
  let config = USER_TYPE_CONFIG[productKey];

  // 如果没有配置，尝试使用降级映射
  if (!config) {
    const fallbackKey = USER_TYPE_FALLBACK_MAP[productKey];
    if (fallbackKey) {
      config = USER_TYPE_CONFIG[fallbackKey];
    }
  }

  // 如果还是没有，返回默认配置
  return config || DEFAULT_USER_CONFIG;
}
